import { EyeInvisibleOutlined } from '@ant-design/icons'
import { useToggle } from 'ahooks'
import { EventEmitter } from 'ahooks/lib/useEventEmitter'
import { Badge, Tooltip } from 'antd'
import cls from 'classnames'
import { isNumber } from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { UserAvatar } from '@/components'
import { RequestedCommentFragment, RequestedPostFragment, useGetViewerQuery } from '@/generated/graphql'

import { PostCommentTextarea } from './post-comment-textarea.component'
import { useCreateComment } from './post-comment.hooks'
import s from './post-comments.module.less'

export interface PostCommentCreateProps {
  post: RequestedPostFragment
  parentComment?: RequestedCommentFragment
  placeholder: string
  autoFocus?: boolean
  onChangeContent$: EventEmitter<string | null>
}

export const PostCommentCreate: React.FC<PostCommentCreateProps> = ({
  post,
  parentComment,
  placeholder,
  autoFocus,
  onChangeContent$,
}) => {
  const [t] = useTranslation()

  const { data: viewer } = useGetViewerQuery()
  const [createComment, { loading }] = useCreateComment()
  const [isAnonymous, { toggle: toggleAnonymous }] = useToggle()

  const anonymousCount = viewer!.viewer.anonymousCount
  const canUseAnonymous = isNumber(anonymousCount) && anonymousCount > 0

  const onSubmit = async (content: string) => {
    await createComment({
      variables: {
        comment: {
          postId: post.postId,
          isAnonymous,
          parentCommentId: parentComment ? parentComment.commentId : null,
          content: content.replace('\n', '\n\n'), // 双换行才能让 markdown 渲染换行,
        },
      },
    })
  }

  const onToggleAnonymous = () => {
    if (!canUseAnonymous) {
      return
    }
    toggleAnonymous()
    onChangeContent$.emit(null)
  }

  const title = canUseAnonymous
    ? isAnonymous
      ? t('post.editor.anonymousCountLeft', { countText: anonymousCount })
      : t('post.editor.publishAnonymously')
    : t('post.editor.anonymousCountIsZero')

  const anonButton = (!parentComment || parentComment.isAnonymous) &&
    (!post.group || post.group.canPostAnonymously) && (
      <Tooltip title={title}>
        <Badge count={isAnonymous && canUseAnonymous ? anonymousCount : 0} size='small'>
          <EyeInvisibleOutlined
            style={{ cursor: canUseAnonymous ? 'pointer' : 'not-allowed' }}
            className={cls(s.actionButton, { [s.active]: isAnonymous && canUseAnonymous })}
            onClick={onToggleAnonymous}
          />
        </Badge>
      </Tooltip>
    )

  return (
    <div className={s.createComment}>
      <UserAvatar className={s.createCommentAvatar} user={isAnonymous && canUseAnonymous ? null : viewer!.viewer} />
      <div className={s.createRight}>
        <PostCommentTextarea
          content={''}
          loading={loading}
          extraActions={anonButton || undefined}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onSubmit={onSubmit}
          onChangeContent$={onChangeContent$}
        />
      </div>
    </div>
  )
}
